import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, CheckCircle2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["Theyyam Type", "Temple", "Theyyam Artist", "Ritual & Performance", "Festival & Event", "Other"];

const inputCls =
  "w-full bg-white border border-charcoal/20 rounded-none px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-vermillion transition-colors duration-300";

const labelCls = "block text-[11px] uppercase tracking-[0.18em] text-charcoal/50 font-medium mb-2";

const EMPTY = { name: "", email: "", instagram: "", website: "", title: "", category: "", theyyam: "", temple: "", description: "" };

export default function PhotoSubmissionModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [declOwnership, setDeclOwnership] = useState(false);
  const [declReview, setDeclReview] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!file) {
      toast.error("Please upload a photo.");
      return;
    }
    if (!declOwnership || !declReview) {
      toast.error("Please confirm both declarations before submitting.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await api.post("/photo-submissions", fd);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setForm(EMPTY);
      setFile(null);
      setDeclOwnership(false);
      setDeclReview(false);
      setDone(false);
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[130] bg-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl max-h-[90vh] bg-ivory text-charcoal flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="photo-form-modal"
          >
            <div className="flex items-center justify-between px-7 md:px-9 pt-7 md:pt-8 pb-5 border-b border-charcoal/10 shrink-0">
              <div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold">Contribute</p>
                <h3 className="mt-2 font-serif font-semibold tracking-tight text-2xl md:text-3xl">Submit Your Photo</h3>
              </div>
              <button
                onClick={close}
                data-testid="photo-form-close"
                aria-label="Close form"
                className="w-9 h-9 rounded-full bg-charcoal/5 text-charcoal flex items-center justify-center transition-colors duration-300 hover:bg-vermillion hover:text-ivory"
              >
                <X size={17} strokeWidth={2} />
              </button>
            </div>

            <div className="overflow-y-auto px-7 md:px-9 py-7" data-lenis-prevent>
              {done ? (
                <div className="flex flex-col items-center text-center gap-4 py-14" data-testid="photo-form-success">
                  <CheckCircle2 className="text-gold" size={44} strokeWidth={1.5} />
                  <p className="font-serif text-2xl font-semibold">Thank you for contributing!</p>
                  <p className="text-charcoal/60 text-sm leading-relaxed max-w-md">
                    Your photo has been received. The Kannur TheyyamNow team will review it personally and contact you via email or Instagram before any use — with full credit to you.
                  </p>
                  <button
                    onClick={close}
                    data-testid="photo-form-done-close"
                    className="mt-4 bg-charcoal text-ivory text-xs uppercase tracking-[0.2em] font-medium px-8 py-4 transition-colors duration-300 hover:bg-vermillion"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="photo-form" className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="pf-name" className={labelCls}>Photographer Name *</label>
                      <input id="pf-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="photo-name-input" />
                    </div>
                    <div>
                      <label htmlFor="pf-email" className={labelCls}>Email Address *</label>
                      <input id="pf-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="photo-email-input" />
                    </div>
                    <div>
                      <label htmlFor="pf-instagram" className={labelCls}>Instagram Profile (Optional)</label>
                      <input id="pf-instagram" value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle or profile link" className={inputCls} data-testid="photo-instagram-input" />
                    </div>
                    <div>
                      <label htmlFor="pf-website" className={labelCls}>Website / Portfolio Link (Optional)</label>
                      <input id="pf-website" value={form.website} onChange={set("website")} placeholder="https://…" className={inputCls} data-testid="photo-website-input" />
                    </div>
                    <div>
                      <label htmlFor="pf-title" className={labelCls}>Photo Title *</label>
                      <input id="pf-title" required value={form.title} onChange={set("title")} placeholder="e.g. Poothadi Thira at dawn" className={inputCls} data-testid="photo-title-input" />
                    </div>
                    <div>
                      <label htmlFor="pf-category" className={labelCls}>Photo Category *</label>
                      <select id="pf-category" required value={form.category} onChange={set("category")} className={inputCls} data-testid="photo-category-select">
                        <option value="" disabled>Select a category</option>
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="pf-theyyam" className={labelCls}>Theyyam Name (Optional)</label>
                      <input id="pf-theyyam" value={form.theyyam} onChange={set("theyyam")} placeholder="e.g. Gulikan" className={inputCls} data-testid="photo-theyyam-input" />
                    </div>
                    <div>
                      <label htmlFor="pf-temple" className={labelCls}>Temple Name (Optional)</label>
                      <input id="pf-temple" value={form.temple} onChange={set("temple")} placeholder="e.g. Parassinikkadavu" className={inputCls} data-testid="photo-temple-input" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="pf-description" className={labelCls}>Description (Optional)</label>
                    <textarea id="pf-description" rows={3} value={form.description} onChange={set("description")} placeholder="Anything we should know about this photo…" className={`${inputCls} resize-none`} data-testid="photo-description-input" />
                  </div>

                  <div>
                    <span className={labelCls}>Upload Photo *</span>
                    <label
                      htmlFor="pf-file"
                      className="flex items-center justify-center gap-3 border border-dashed border-charcoal/25 bg-white px-4 py-8 cursor-pointer text-charcoal/50 text-sm transition-colors duration-300 hover:border-vermillion hover:text-vermillion"
                      data-testid="photo-file-dropzone"
                    >
                      {file ? <ImageIcon size={17} /> : <Upload size={17} />}
                      {file ? file.name : "Choose an image (JPG, PNG, WebP — max 12 MB)"}
                    </label>
                    <input
                      id="pf-file"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid="photo-file-input"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </div>

                  <div className="space-y-3 border border-gold/40 bg-goldlight/10 p-5">
                    <label className="flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer" data-testid="photo-declaration-1">
                      <input type="checkbox" checked={declOwnership} onChange={(e) => setDeclOwnership(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#B21807] shrink-0" />
                      I confirm that I own this photo or have permission to share it with Kannur TheyyamNow.
                    </label>
                    <label className="flex items-start gap-3 text-sm text-charcoal/80 cursor-pointer" data-testid="photo-declaration-2">
                      <input type="checkbox" checked={declReview} onChange={(e) => setDeclReview(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#B21807] shrink-0" />
                      I understand that all submissions will be reviewed by the Kannur TheyyamNow team before publication.
                    </label>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={sending}
                    whileTap={{ scale: 0.98 }}
                    data-testid="photo-submit-button"
                    className="w-full bg-vermillion text-ivory text-xs uppercase tracking-[0.22em] font-semibold px-8 py-4 transition-colors duration-300 hover:bg-blood disabled:opacity-60"
                  >
                    {sending ? "Submitting…" : "Submit Photo"}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
